import * as api from "../../api/class";
import * as breakpoints from "../../breakpoints";
import * as classTileColors from "../../constants/classTileColors";
import * as colors from "../../constants/colors";
import * as languages from "../../constants/languages";
import { css, StyleSheet } from "aphrodite";
import categoryColors from "../../constants/categoryColors";
import { english, spanish } from "../../constants/strings";
import { handleUnknownError } from "../../lib/errorHandler";
import CertificateModal from "../CertificateModal";
import CoursesContainer from "../CoursesContainer";
import FilterBar from "../FilterBar";
import { NotificationManager } from "react-notifications";
import React, { PureComponent } from "react";
import Page from "../Page";
import PropTypes from "prop-types";
import { uniqueObjectsByKey } from "../../lib/util";
import { Container, Column, Row } from "../../grid";
import { browserHistory } from "react-router";

const styles = StyleSheet.create({
	content: {
		"@media print": {
			display: "none",
		},
	},
	noScrollContent: {
		[breakpoints.mobileOnly]: {
			maxHeight: "50vh",
			overflow: "hidden",
		},
	},
	mainButton: {
		borderRadius: 20,
		border: "none",
		backgroundColor: colors.pink,
		fontWeight: "bold",
		fontFamily: "priori-sans",
		fontSize: 15,
		lineHeight: 1.4,
		marginBottom: 24,
		paddingTop: 10,
		paddingBottom: 10,
		paddingLeft: 24,
		paddingRight: 24,
	},
});

export default class CatalogPage extends PureComponent {
	static propTypes = {
		language: PropTypes.string.isRequired,
		pathname: PropTypes.string.isRequired,
	};

	constructor(props) {
		super(props);

		this.strings = props.language === languages.es ? spanish : english;

		this.state = {
			courses: [],
			filteredCourses: [],
			filterOptions: [],
			selectedFilter: "",
			certificateCourse: {},
		};
	}

	componentDidMount = () => {
		this._loadCourses();
	};

	_categoryColors = () => {
		const catColors = categoryColors;
		const colors = Object.keys(classTileColors).map(colorName => classTileColors[colorName]);
		let colorIndex = Object.keys(categoryColors).length;
		this.state.filterOptions.forEach(opt => {
			if (
				opt.label && // eslint-disable-next-line
				!(opt.label.toLowerCase().replace(/[&\/\\#,+()$~%.'":*?<>{}\s’]/g, "") in catColors)
			) {
				// eslint-disable-next-line
				catColors[opt.label.toLowerCase().replace(/[&\/\\#,+()$~%.'":*?<>{}\s’]/g, "")] =
					colors[colors.length > colorIndex ? colorIndex : colorIndex - colors.length];
				colorIndex += 1;
			}
		});
		return catColors;
	};

	_loadCourses = () => {
		api
			.getClasses(null, null, this.props.language === languages.es ? languages.es : null)
			.then(response => {
				let categories = response.classes.map(c => ({
					label: c.primaryCategoryName,
					value: c.primaryCategoryID,
				}));
				categories = uniqueObjectsByKey(categories, "value");
				let types = response.classes.map(c => ({
					label: this.strings.type[c.type],
					value: c.type,
				}));
				types = uniqueObjectsByKey(types, "value");
				this.setState({
					courses: response.classes,
					filteredCourses: this._filteredCourses(
						response.classes,
						this.state.selectedFilter,
					),
					filterOptions: [
						{
							label: this.strings.Class_Topics,
							value: this.strings.Class_Topics,
							subheading: true,
						},
						...categories,
						{
							label: this.strings.Format,
							value: this.strings.Format,
							subheading: true,
						},
						...types,
					],
				});
			})
			.catch(err => {
				handleUnknownError(err);
				NotificationManager.error(this.strings.errors.failedToLoadClasses);
			});
	};

	_filteredCourses = (courses, filter) => {
		if (filter === "") {
			return courses;
		}
		return courses.filter(c => {
			const categories = c.categories ? c.categories.map(cat => cat.id) : [];
			const courseCategories = [c.primaryCategoryID, ...categories];
			return courseCategories.indexOf(filter) > -1 || c.type === filter;
		});
	};

	_setFilter = selectedFilter => {
		this.setState({
			selectedFilter,
			filteredCourses: this._filteredCourses(this.state.courses, selectedFilter),
		});
	};

	_showCertificate = course => {
		this.setState({
			showCertificate: true,
			certificateCourse: course,
		});
	};

	_hideCertificate = () => {
		this.setState({
			showCertificate: false,
		});
	};

	_updateBookmark = course => {
		this.setState({
			courses: this.state.courses.map(c => {
				return {
					...c,
					bookmarked: course.id === c.id ? !c.bookmarked : c.bookmarked,
				};
			}),
			filteredCourses: this.state.filteredCourses.map(c => {
				return {
					...c,
					bookmarked: course.id === c.id ? !c.bookmarked : c.bookmarked,
				};
			}),
		});
	};

	render() {
		return (
			<Page
				activeTab={this.strings.tabNames.CATALOG}
				pageTitle={this.strings.tabNames.CATALOG}
				language={this.props.language}
				currentRoute={this.props.pathname}
				modalShowing={this.state.showCertificate}
			>
				<div
					className={css(
						this.state.showCertificate
							? [styles.noScrollContent, styles.content]
							: styles.content,
					)}
				>
					<Container>
						<Row>
							<Column>
								<button
									onClick={() => {
										browserHistory.push({
											pathname: "/catalog/live",
											query: { language: this.props.language },
										});
									}}
									className={css(styles.mainButton)}
								>
									{this.strings.liveClassCalendar.toUpperCase()}
								</button>
							</Column>
						</Row>
					</Container>

					<FilterBar
						language={this.props.language}
						options={[
							{ value: "", label: `- ${this.strings.Any} -` },
							...this.state.filterOptions,
						]}
						selectValue={this._setFilter}
						selectedValue={this.state.selectedFilter}
					/>
					<CoursesContainer
						categoryColors={this._categoryColors()}
						categoryClicked={this._setFilter}
						courses={this.state.filteredCourses}
						filter=""
						language={this.props.language}
						showCertificate={this._showCertificate}
						updateBookmark={this._updateBookmark}
					/>
				</div>
				<CertificateModal
					showing={this.state.showCertificate}
					classId={this.state.certificateCourse.id}
					dismiss={this._hideCertificate}
					language={this.props.language}
				/>
			</Page>
		);
	}
}



// WEBPACK FOOTER //
// ./src/components/pages/CatalogPage.js